import { z } from "zod";
import { ActorRefSchema, LocalizedTextSchema, TimestampSchema } from "./common.js";
import { IdSchema, NamespacedNameSchema } from "./ids.js";
export const PermissionSchema = NamespacedNameSchema;
export const PermissionPatternSchema = z.union([
    z.literal("*"),
    z.string().regex(/^[a-z][a-z0-9]*(?:\.[a-z][a-zA-Z0-9]*)*\.\*$/),
    PermissionSchema,
]);
/** True if a granted permission (or pattern) covers the required permission. */
export function permissionMatches(granted, required) {
    if (granted === "*")
        return true;
    if (granted.endsWith(".*"))
        return required.startsWith(granted.slice(0, -1));
    return granted === required;
}
/** A permission introduced by core or a module. */
export const PermissionDefSchema = z.object({
    name: PermissionSchema,
    description: LocalizedTextSchema,
});
/** Scope levels, from widest to narrowest. A grant covers its scope and everything below. */
export const SCOPE_LEVELS = ["world", "company", "building", "floor", "project"];
export const ScopeLevelSchema = z.enum(SCOPE_LEVELS);
/** Where a grant applies. `id` is null only for `world`. */
export const ScopeRefSchema = z.discriminatedUnion("level", [
    z.object({ level: z.literal("world"), id: z.null() }),
    z.object({ level: z.literal("company"), id: IdSchema }),
    z.object({ level: z.literal("building"), id: IdSchema }),
    z.object({ level: z.literal("floor"), id: IdSchema }),
    z.object({ level: z.literal("project"), id: IdSchema }),
]);
/**
 * The full location of the resource being acted on. A grant matches when its scope equals the
 * path's id at that level (or is `world`). `companyId: null` means a world-scoped resource.
 */
export const ScopePathSchema = z.object({
    companyId: IdSchema.nullable(),
    buildingId: IdSchema.optional(),
    floorId: IdSchema.optional(),
    projectId: IdSchema.optional(),
});
/** True if a grant at `scope` covers a resource located at `path`. */
export function scopeCovers(scope, path) {
    switch (scope.level) {
        case "world":
            return true;
        case "company":
            return path.companyId === scope.id;
        case "building":
            return path.buildingId === scope.id;
        case "floor":
            return path.floorId === scope.id;
        case "project":
            return path.projectId === scope.id;
    }
}
/** Keys of the built-in roles (DESIGN §5). Owner is seeded via CLI, never via the UI. */
export const BUILT_IN_HUMAN_ROLES = [
    "owner",
    "world_admin",
    "company_owner",
    "project_manager",
    "investor",
    "member",
    "guest",
];
export const BUILT_IN_AGENT_ROLES = [
    "personal_assistant",
    "department_lead",
    "worker",
    "architect",
    "interior_designer",
];
export const RoleSchema = z.object({
    id: IdSchema,
    /** Stable key; built-in roles use the constants above. */
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    name: LocalizedTextSchema,
    subjectKind: z.enum(["user", "agent"]),
    permissions: z.array(PermissionPatternSchema),
    builtIn: z.boolean(),
    /** null = available everywhere; otherwise a custom role of one company. */
    companyId: IdSchema.nullable(),
});
export const SubjectRefSchema = z.object({ kind: z.enum(["user", "agent"]), id: IdSchema });
export const GrantSchema = z.object({
    id: IdSchema,
    subject: SubjectRefSchema,
    roleId: IdSchema,
    scope: ScopeRefSchema,
    grantedBy: ActorRefSchema,
    grantedAt: TimestampSchema,
    revokedAt: TimestampSchema.nullable(),
});
//# sourceMappingURL=permissions.js.map